// src/oura/components/client/AskDrawer.tsx
//
// One drawer for everything on the Client tab's two ask cards (drawer specs
// v1.1, A-D, A-E, A-F, A-L): one <Drawer> shell and one sheet inside it,
// keyed per item, so the viewer walks the group a row sat in with previous
// and next (J and K), and never waits on a drawer closing and opening.
//
//   - a question we sent (Questions to the client, or a pack): ItemSheet.
//     Its move leads (accept their wording, revise and send, reply), their
//     words follow, and what we asked and the conversation fold away.
//   - a change the client asked for (Requests): ChangeSheet. Reply from
//     oura@, file it as the month's small change, mark it done.
//   - a call they asked for: CallSheet. Mark it scheduled, or close it.
//   - a pack, from a question's "Sent in" line: PackSheet (PackSheet.tsx).
//
// A question opened from a call or a pack is a sub-view: "‹ Call request"
// takes the viewer back, and Escape goes back before it closes. An action
// finishes the item when the item has left its group on the next read; the
// foot then offers Next (or Done on the last one). Writing is kept per item
// while moving and on close.
//
// The drawer reads the screen's own lists on every render, so an answer that
// lands on the next poll shows up in an open drawer too. Phase 1: nothing
// here writes to a Google Doc, and there is no chase button: a late question
// gets a reminder through "Write to {client}".
//
// Targets, keys and group snapshots live in askTargets.ts.

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { CalendarClockIcon, ExternalLinkIcon, FileTextIcon, ImageIcon, MessageSquarePlusIcon, PaperclipIcon, PhoneIcon, SendIcon, SparklesIcon } from "lucide-react";
import type { TrackerTask } from "../../tracker";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { useSnackbar } from "@hook/use-snackbar";
import {
    AREA_TEXT,
    ArrowLink,
    Drawer,
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    DrawerQuote,
    FOCUS_RING,
    FamilyChip,
    PillButton,
    SURFACE,
    TYPE,
    Tag,
    dataSel,
    drawerNav,
    drawerSequence,
    returnFocusChain,
    useDrawerDrafts,
    useDrawerFocusClaim,
    type DrawerBack,
    type DrawerConfirm,
    type DrawerFootMessage,
    type DrawerReturnFocus,
    type DrawerStatus,
    type Tone,
} from "@oura/ui";
import { REPLY_PICTURES_ON, ReplyPictureGrid, picturesWord } from "../ReplyPictures";
import { calendarLateText, extraSmallChangeIds, fromAgoText, trimText } from "@oura/docs";
import {
    acceptSuggestedEdit,
    DEMO_STAFF,
    everyReply,
    malaysianMonthNow,
    postMessage,
    replyToChangeRequest,
    resolveThread,
    updateCallRequest,
    updateChangeRequest,
    updatePack,
    uploadFile,
    uploadReplyPicture,
    type CallRequest,
    type ChangeRequest,
    type Pack,
    type PackItem,
    type ReviewItemKind,
    type Thread,
    type ThreadMessage,
} from "@service/aeo/clientReview";
import { reviseItem } from "@service/aeo/clientReviewAgent";
import type { AskGroups } from "./asks";
import { ASK_GROUP, askKey, askSeqFor, keyTarget, type AskSeq, type AskTarget } from "./askTargets";
import { askedText, changeRequestsChanged, OURA_INBOX, repliedText, requestErrorText, requesterName, useStaffSignature, type RequestGroups } from "./changeRequests";
import { flowDone, flowMessage, lastLineFor, nextLive, useSheetVoice, type SheetFlow } from "./drawerFlow";
import { PackSheet } from "./PackSheet";
import { Brief, ClientMessage } from "../../requests/RequestBrief";
import { AddAsExtraWork } from "../../delivery/AddAsExtraWork";
import { DELIVERY_ON } from "../../delivery/flag";
import { StaffComposer, StaffThreadMessages } from "./StaffThread";

export type { AskSeq, AskTarget } from "./askTargets";

const KIND_TAG: Record<ReviewItemKind, { label: string; tone: Tone }> = {
    choose: { label: "Answer", tone: "teal" },
    approve: { label: "Approve", tone: "indigo" },
    provide_file: { label: "Send a file", tone: "orange" },
};

/** "September", for "File as September's small change". */
function monthName(ym: string | null | undefined): string | null {
    const m = ym ? /^(\d{4})-(\d{2})$/.exec(ym) : null;
    if (!m) return null;
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleString("en-GB", { month: "long", timeZone: "UTC" });
}

/** Messages people wrote: never a version bump or a decision line. */
const said = (m: ThreadMessage) => m.kind === "message" || m.kind === "suggested_edit";

/* ------------------------------------------------------------------------- *
 * The host
 * ------------------------------------------------------------------------- */

interface Lists {
    packs: Pack[];
    threads: Thread[];
    calls: CallRequest[];
    /** The client's change requests, every state. */
    changes: ChangeRequest[];
    /** The Questions card's groups: who moves next on each question. */
    groups: AskGroups;
    /** The Requests card's groups. */
    requestGroups: RequestGroups;
    taskById: ReadonlyMap<string, TrackerTask>;
}

function findItem(packs: Pack[], itemId: string): { item: PackItem; pack: Pack } | null {
    for (const pack of packs) {
        const item = pack.items.find((i) => i.id === itemId);
        if (item) return { item, pack };
    }
    return null;
}

export function AskDrawer({
    target,
    seq,
    onOpen,
    onClose,
    businessId,
    onChanged,
    hasG5,
    itemTitle,
    ...lists
}: Lists & {
    target: AskTarget | null;
    /** The group the row sat in; null for a link or a Packs row, which open with the item's current group. */
    seq: AskSeq | null;
    onOpen: (target: AskTarget, seq: AskSeq | null) => void;
    onClose: () => void;
    businessId: string;
    onChanged: () => Promise<void>;
    /** G5 is in the client's contract: the small-change line shows. Null when the contract could not be read. */
    hasG5: boolean | null;
    /** A call's question by title. */
    itemTitle: (itemId: string) => string | null;
}) {
    const snackbar = useSnackbar();
    const drafts = useDrawerDrafts();
    const [busy, setBusy] = useState(false);
    const { packs, threads, calls, changes, groups, requestGroups } = lists;

    const index = useMemo(() => {
        const group = new Map<string, string>();
        groups.needsUs.forEach((a) => group.set(`item:${a.item.id}`, "asks.needs_us"));
        groups.waiting.forEach((a) => group.set(`item:${a.item.id}`, "asks.waiting"));
        groups.answered.forEach((a) => group.set(`item:${a.item.id}`, "asks.answered"));
        for (const p of packs) {
            group.set(`pack:${p.id}`, p.status === "draft" ? "asks.not_sent" : "packs.sent");
            if (p.status === "draft") p.items.forEach((i) => group.set(`item:${i.id}`, "asks.not_sent"));
        }
        requestGroups.needsUs.forEach((r) => group.set(`${r.kind}:${r.id}`, "requests.needs_us"));
        requestGroups.scheduled.forEach((r) => group.set(`${r.kind}:${r.id}`, "requests.scheduled"));
        requestGroups.done.forEach((r) => group.set(`${r.kind}:${r.id}`, "requests.done"));
        return group;
    }, [groups, packs, requestGroups]);

    /**
     * Who we write to when a question's own thread has no word from them yet:
     * the client's latest name on any thread or request, else "the client".
     */
    const clientName = useMemo(() => {
        const named: Array<{ at: string; name: string }> = [];
        for (const t of threads) for (const m of t.messages) if (m.authorKind === "client" && said(m)) named.push({ at: m.at, name: m.authorName });
        for (const c of changes) named.push({ at: c.at, name: requesterName(c) });
        named.sort((a, b) => b.at.localeCompare(a.at));
        return named.find((n) => n.name && n.name !== "The client")?.name ?? "the client";
    }, [threads, changes]);

    /** The title a key's row carries, for "Next: …" and for Back. */
    const titleOfKey = (key: string): string | null => {
        const t = keyTarget(key);
        if (t.kind === "item") return findItem(packs, t.itemId)?.item.title ?? null;
        if (t.kind === "pack") return packs.find((p) => p.id === t.packId)?.title ?? null;
        if (t.kind === "change") {
            const c = changes.find((x) => x.id === t.changeId);
            return c ? `“${trimText(c.note.trim(), CHANGE_TITLE_MAX)}”` : null;
        }
        const call = calls.find((x) => x.id === t.callId);
        if (!call) return null;
        const about = call.itemId ? itemTitle(call.itemId) : null;
        return about ? `Call about “${about}”` : `${requesterName(call)} asked for a call`;
    };

    const baseKey = target ? askKey(target) : "";
    // A link, or a Packs row, came from no group: freeze the item's current
    // group now, so the walk does not shift as items finish.
    const [frozen, setFrozen] = useState<AskSeq | null>(null);
    const active: AskSeq | null = !target ? null : seq?.keys.includes(baseKey) ? seq : frozen?.keys.includes(baseKey) ? frozen : null;
    if (target && !active) {
        const gid = index.get(baseKey);
        const derived = gid ? askSeqFor(gid, groups, requestGroups) : null;
        setFrozen(derived && derived.keys.includes(baseKey) ? derived : { groupId: gid ?? "", label: gid ? ASK_GROUP[gid]?.label ?? "" : "", keys: [baseKey] });
    }

    // Sub-views over the item the row opened: a question from a call or a pack, a pack from a question.
    const [subs, setSubs] = useState<{ base: string; views: AskTarget[] }>({ base: "", views: [] });
    const views = subs.base === baseKey ? subs.views : [];
    const current: AskTarget | null = views.length > 0 ? views[views.length - 1] : target;
    const currentKey = current ? askKey(current) : "";
    const push = (t: AskTarget) => setSubs({ base: baseKey, views: [...views, t] });
    const pop = () => setSubs({ base: baseKey, views: views.slice(0, -1) });

    const inSnap = (key: string) => Boolean(active) && index.get(key) === active?.groupId;
    const titleOf = (key: string) => (inSnap(key) ? titleOfKey(key) : null);
    const keys = active?.keys ?? [];
    const sequence = target ? drawerSequence(keys, baseKey, titleOf) : null;
    const move = (key: string) => {
        setBusy(false);
        onOpen(keyTarget(key), active);
    };
    const nav = sequence && active && views.length === 0 ? drawerNav(sequence, { group: active.label, onMove: move, keys: true, disabled: busy }) : undefined;
    const next = target ? nextLive(keys, baseKey, titleOf) : null;

    const close = () => {
        if (drafts.get(currentKey).trim()) snackbar.showSuccess("Kept. Your reply is not sent yet.");
        setBusy(false);
        setSubs({ base: "", views: [] });
        onClose();
    };

    const flow: SheetFlow = {
        inGroup: inSnap(baseKey),
        group: active?.label ?? "",
        left: sequence?.left ?? 0,
        next,
        onNext: () => {
            if (next) move(next.id);
        },
        onDone: close,
        lastLine: active?.groupId === "asks.not_sent" ? "Nothing else to send." : lastLineFor(active?.label ?? ""),
        onBusy: setBusy,
        sub: views.length > 0,
    };

    const back: DrawerBack | undefined =
        views.length > 0
            ? (() => {
                const prev = views.length > 1 ? views[views.length - 2] : target;
                const label = prev?.kind === "call" ? "Call request" : prev?.kind === "change" ? "Change request" : (prev && titleOfKey(askKey(prev))) || "Back";
                return { label, onBack: pop };
            })()
            : undefined;

    // Focus on close: the row, found again in whatever group it now sits in;
    // its group's head when that is folded; else the opener. Kept from the
    // last open render, since the shell looks it up after the close.
    const lastReturn = useRef<DrawerReturnFocus | undefined>(undefined);
    if (target) {
        const rowSel =
            target.kind === "item"
                ? dataSel("ask-id", target.itemId)
                : target.kind === "change"
                    ? dataSel("change-id", target.changeId)
                    : target.kind === "call"
                        ? dataSel("call-id", target.callId)
                        : dataSel("pack-id", target.packId);
        const nowIn = index.get(baseKey);
        lastReturn.current = returnFocusChain(rowSel, nowIn && dataSel("group-head", nowIn), active?.groupId && dataSel("group-head", active.groupId));
    }

    let sheet: ReactNode = null;
    if (current) {
        const sheetKey = `${views.length}:${currentKey}`;
        const draft = drafts.get(currentKey);
        const setDraft = (text: string) => drafts.set(currentKey, text);
        if (current.kind === "item") {
            const found = findItem(packs, current.itemId);
            sheet = found ? (
                <ItemSheet
                    key={sheetKey}
                    item={found.item}
                    pack={found.pack}
                    thread={threads.find((t) => t.itemId === found.item.id) ?? null}
                    groupId={index.get(`item:${found.item.id}`) ?? null}
                    hasEdit={groups.needsUs.find((a) => a.item.id === found.item.id)?.hasEdit ?? false}
                    clientName={clientName}
                    task={lists.taskById.get(found.item.taskId)}
                    businessId={businessId}
                    flow={flow}
                    draft={draft}
                    setDraft={setDraft}
                    onSub={push}
                    onChanged={onChanged}
                />
            ) : (
                <Gone key={sheetKey} title="Question" />
            );
        } else if (current.kind === "change") {
            const change = changes.find((c) => c.id === current.changeId) ?? (current.change?.id === current.changeId ? current.change : undefined);
            sheet = change ? (
                <ChangeSheet
                    key={sheetKey}
                    change={change}
                    all={changes}
                    hasG5={hasG5}
                    flow={flow}
                    draft={draft}
                    setDraft={setDraft}
                    onChanged={onChanged}
                />
            ) : (
                <Gone key={sheetKey} title="Change request" />
            );
        } else if (current.kind === "call") {
            const call = calls.find((c) => c.id === current.callId);
            sheet = call ? (
                <CallSheet
                    key={sheetKey}
                    call={call}
                    item={call.itemId ? findItem(packs, call.itemId)?.item ?? null : null}
                    flow={flow}
                    onSub={push}
                    onChanged={onChanged}
                />
            ) : (
                <Gone key={sheetKey} title="Call request" />
            );
        } else {
            const pack = packs.find((p) => p.id === current.packId);
            sheet = pack ? (
                <PackSheet
                    key={sheetKey}
                    pack={pack}
                    itemGroup={(itemId) => {
                        const gid = index.get(`item:${itemId}`);
                        return gid ? ASK_GROUP[gid] ?? null : null;
                    }}
                    flow={flow}
                    onSub={push}
                    onChanged={onChanged}
                />
            ) : (
                <Gone key={sheetKey} title="Pack" />
            );
        }
    }

    return (
        <Drawer
            open={Boolean(target)}
            onClose={close}
            nav={nav}
            back={back}
            itemKey={target ? [baseKey, ...views.map(askKey)].join(">") : undefined}
            returnFocus={lastReturn.current}
        >
            {sheet}
        </Drawer>
    );
}

/** An item the lists no longer hold. */
function Gone({ title }: { title: string }) {
    return (
        <>
            <DrawerHead title={title} />
            <DrawerBlock>
                <p className="m-0 text-gray-700">This is no longer here.</p>
            </DrawerBlock>
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * Shared pieces
 * ------------------------------------------------------------------------- */

/** Their words, newest last: up to 3, then "Show N earlier". */
function Quotes({ messages }: { messages: ThreadMessage[] }) {
    const [all, setAll] = useState(false);
    const hidden = all ? 0 : Math.max(0, messages.length - 3);
    const shown = messages.slice(hidden);
    return (
        <div className="space-y-2">
            {hidden > 0 && (
                <PillButton variant="text" aria-expanded={false} onClick={() => setAll(true)}>
                    Show {hidden} earlier
                </PillButton>
            )}
            {shown.map((m) => (
                <DrawerQuote key={m.id} who={m.authorName} at={m.at}>
                    {m.body.trim() || (m.suggestedEdit ? "Suggested an edit to the wording." : m.fileName ? `Sent a file: ${m.fileName}` : "")}
                </DrawerQuote>
            ))}
        </div>
    );
}

/** The files a client added to a request, each opening in a new tab. */
function FileList({ files }: { files: ChangeRequest["files"] }) {
    if (files.length === 0) return null;
    return (
        <ul aria-label="Files they added" className="list-plain m-0 mt-2 space-y-1.5 p-0">
            {files.map((f) => {
                const pdf = /\.pdf$/i.test(f.name);
                const Icon = pdf ? FileTextIcon : ImageIcon;
                return (
                    <li key={f.fileKey} className={cn(TYPE.tc, "flex min-w-0 items-center gap-2")}>
                        <Icon aria-hidden size={14} className={cn("shrink-0", pdf ? "text-red-600" : "text-blue-600")} />
                        {f.url ? (
                            <a
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={f.name}
                                className={cn(
                                    "relative inline-flex min-w-0 items-center gap-1 rounded font-medium text-blue-600 hover:underline after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']",
                                    FOCUS_RING,
                                )}
                            >
                                <span className="truncate">{f.name}</span>
                                <ExternalLinkIcon aria-hidden size={12} className="shrink-0" />
                                <span className="sr-only">(opens in a new tab)</span>
                            </a>
                        ) : (
                            <span title={f.name} className="min-w-0 truncate text-gray-900">
                                {f.name}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/** The paperclip and a count, for a request's chips. */
function Clip({ count, names }: { count: number; names: string[] }) {
    if (count === 0) return null;
    return (
        <span title={names.join(", ")} className={cn(TYPE.tm, "inline-flex items-center gap-1 tabular-nums text-gray-500")}>
            <PaperclipIcon aria-hidden size={12} className="shrink-0 text-[#8E8E93]" />
            {count}
            <span className="sr-only">{count === 1 ? " file" : " files"}</span>
        </span>
    );
}

/**
 * A callback ref for a sheet's reply field: it claims the drawer's focus
 * (on open and on a move) while `claim` is on, and keeps the node so a failed
 * send or a switch to write mode can put the cursor back in it.
 */
function useFieldRef(claim: boolean) {
    const claimRef = useDrawerFocusClaim();
    const node = useRef<HTMLTextAreaElement | null>(null);
    const ref = useCallback(
        (el: HTMLTextAreaElement | null) => {
            node.current = el;
            if (claim) return claimRef(el);
        },
        [claim, claimRef],
    );
    const focus = useCallback(() => node.current?.focus(), []);
    return { ref, focus };
}

/* ------------------------------------------------------------------------- *
 * A-D: a question we sent
 * ------------------------------------------------------------------------- */

type ItemBusy = "accept" | "revise" | "send" | "resolve" | "draft" | null;

function ItemSheet({
    item,
    pack,
    thread,
    groupId,
    hasEdit,
    clientName,
    task,
    businessId,
    flow,
    draft,
    setDraft,
    onSub,
    onChanged,
}: {
    item: PackItem;
    pack: Pack;
    thread: Thread | null;
    /** The group the question sits in now: "asks.needs_us", "asks.waiting", "asks.answered", "asks.not_sent". */
    groupId: string | null;
    hasEdit: boolean;
    /** Who to write to when this thread has no word from them yet. */
    clientName: string;
    task: TrackerTask | undefined;
    businessId: string;
    flow: SheetFlow;
    draft: string;
    setDraft: (text: string) => void;
    onSub: (t: AskTarget) => void;
    onChanged: () => Promise<void>;
}) {
    const [saidText, setSaid] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [busy, setBusyState] = useState<ItemBusy>(null);
    const [confirm, setConfirm] = useState<"resolve" | null>(null);
    const [writing, setWriting] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const voice = useSheetVoice(setSaid, setMessage);

    const messages = (thread?.messages ?? []).filter(said);
    const lastClient = [...messages].reverse().find((m) => m.authorKind === "client") ?? null;
    const lastStaffIndex = messages.map((m) => m.authorKind).lastIndexOf("staff");
    const theirSince = messages.slice(lastStaffIndex + 1).filter((m) => m.authorKind === "client");
    const lastStaff = lastStaffIndex >= 0 ? messages[lastStaffIndex] : null;
    const editMsg = [...messages].reverse().find((m) => m.suggestedEdit && !m.suggestedEdit.accepted) ?? null;
    const name = lastClient?.authorName ?? item.decidedBy ?? clientName;
    const draftPack = pack.status === "draft";
    const needs = groupId === "asks.needs_us";
    const needsState: "edit" | "changes" | "message" | null = !needs ? null : hasEdit && editMsg ? "edit" : item.decision === "changes_requested" ? "changes" : "message";
    // The field is block 1 in a reply we owe with no other move, and in write mode.
    const composing = writing || needsState === "message";
    const field = useFieldRef(composing);
    const canWrite = Boolean(thread) && !draftPack;

    const setBusy = (b: ItemBusy) => {
        setBusyState(b);
        flow.onBusy?.(b !== null);
    };

    /** One write, then the screen reads again; `said` once it has, so Next shows only once the item has moved. */
    const run = async (what: Exclude<ItemBusy, null>, work: () => Promise<string | null>, failed: string) => {
        if (busy) return;
        setBusy(what);
        setMessage(null);
        setSaid(null);
        try {
            const done = await work();
            await onChanged();
            if (done) voice.said(done);
        } catch {
            voice.note({ tone: "error", text: failed });
        } finally {
            setBusy(null);
            setConfirm(null);
        }
    };

    const send = async (asWrite: boolean) => {
        const text = draft.trim();
        if ((!text && !file) || !thread || busy) return;
        setBusy("send");
        setMessage(null);
        setSaid(null);
        setSendError(null);
        try {
            const fileKey = file ? (await uploadFile(businessId, file)).fileKey : null;
            await postMessage(thread.id, { authorKind: "staff", authorName: DEMO_STAFF.name, body: text, fileKey });
            setDraft("");
            setFile(null);
            await onChanged();
            if (asWrite) {
                setWriting(false);
                voice.note({ tone: "success", text: `Sent to ${name}.` });
            } else {
                voice.said("Reply sent.");
            }
        } catch {
            setSendError("Your message did not send. It is still here. Try again.");
            field.focus();
        } finally {
            setBusy(null);
        }
    };

    const accept = () =>
        void run(
            "accept",
            async () => {
                if (!editMsg) return null;
                await acceptSuggestedEdit(item.id, editMsg.id, DEMO_STAFF.name);
                return "Accepted their wording.";
            },
            "Their wording was not accepted. Try again.",
        );

    const revise = () =>
        void run(
            "revise",
            async () => {
                if (!thread) return null;
                const r = await reviseItem(item, thread);
                if (r.questionForClient) {
                    await postMessage(thread.id, { authorKind: "staff", authorName: DEMO_STAFF.name, body: r.questionForClient });
                    return "Sent a question back.";
                }
                await updatePack(item.packId, { items: [{ taskId: item.taskId, kind: item.kind, title: item.title, why: item.why, tags: item.tags, current: item.current, proposed: r.proposed, outline: item.outline, options: item.options }] }, DEMO_STAFF.name);
                await postMessage(thread.id, { authorKind: "staff", authorName: DEMO_STAFF.name, body: `${r.understood} I have updated the wording above.` });
                return "Sent the new wording.";
            },
            "That did not send. Try again in a moment.",
        );

    const resolve = () =>
        void run(
            "resolve",
            async () => {
                if (!thread) return null;
                await resolveThread(thread.id);
                return "Resolved.";
            },
            "It was not resolved. Try again.",
        );

    /** Oura drafts into the field; a person sends. */
    const draftWithOura = async () => {
        if (!thread || busy) return;
        setBusy("draft");
        try {
            const r = await reviseItem(item, thread);
            setDraft(r.questionForClient ?? `${r.understood} Here is the revised wording:\n\n${r.proposed}`);
            field.focus();
        } catch {
            setMessage({ tone: "error", text: "Oura could not draft this. Write the reply by hand." });
        } finally {
            setBusy(null);
        }
    };

    /* Header */
    const late = item.decision === "pending" ? calendarLateText(item.lateDays) : null;
    const replyBy = dayDate(item.replyBy);
    const decided = dayDate(item.decidedAt);
    const DECIDED: Partial<Record<PackItem["decision"], string>> = { approved: "Approved", deferred: "Deferred", changes_requested: "Changes requested" };
    let status: DrawerStatus;
    if (draftPack) status = { tone: ASK_GROUP["asks.not_sent"].tone, label: "Not sent yet", clock: { text: "The client can't see it" } };
    else if (needs) status = { tone: ASK_GROUP["asks.needs_us"].tone, label: "Needs us", clock: lastClient ? { text: `${name} replied ${fromAgoText(lastClient.at) ?? ""}`.trim() } : null };
    else if (groupId === "asks.waiting")
        status = {
            tone: ASK_GROUP["asks.waiting"].tone,
            label: "Waiting on client",
            // The date in words, not only in a tooltip: touch, keyboard and
            // screen-reader users never see a title attribute.
            clock: late
                ? { text: replyBy ? `Was due ${replyBy}, ${late}` : late, late: true }
                : replyBy
                    ? { text: `Reply by ${replyBy}` }
                    : dayDate(pack.publishedAt)
                        ? { text: `Sent ${dayDate(pack.publishedAt)}` }
                        : null,
        };
    else
        status = {
            tone: ASK_GROUP["asks.answered"].tone,
            label: "Answered",
            clock: decided ? { text: DECIDED[item.decision] ? `${DECIDED[item.decision]} ${decided}` : decided } : null,
        };
    const kind = KIND_TAG[item.kind];

    /* Block 1: our move */
    const quoteBlock = theirSince.length > 0 && <Quotes messages={theirSince} />;
    const composer = (label: string) => (
        <StaffComposer
            value={draft}
            onChange={(t) => {
                setDraft(t);
                if (sendError) setSendError(null);
            }}
            label={label}
            placeholder={writing ? "Your message" : "Your reply"}
            onSubmit={() => void send(writing)}
            fieldRef={field.ref}
            error={sendError}
            attach={{ file, onFile: setFile, onError: (t) => setSendError(t) }}
            above={
                !writing && thread ? (
                    <PillButton variant="text" icon={SparklesIcon} busy={busy === "draft"} disabled={busy !== null && busy !== "draft"} onClick={() => void draftWithOura()}>
                        Draft with Oura
                    </PillButton>
                ) : undefined
            }
        />
    );
    let block1: ReactNode = null;
    if (writing) {
        // What we last said stays in view while the reminder is written.
        const lastOurs = !quoteBlock && groupId === "asks.waiting" && lastStaff && (
            <DrawerQuote who={lastStaff.authorName} at={lastStaff.at}>
                {lastStaff.body}
            </DrawerQuote>
        );
        block1 = (
            <DrawerBlock label={`Write to ${name}`} busy={busy === "send"}>
                {(quoteBlock || lastOurs) && <div className="mb-3 mt-1">{quoteBlock || lastOurs}</div>}
                {composer(`Write to ${name}`)}
            </DrawerBlock>
        );
    } else if (needsState === "edit" && editMsg?.suggestedEdit) {
        block1 = (
            <DrawerBlock label="Their wording" busy={busy === "accept"}>
                {(editMsg.suggestedEdit.before || item.proposed) && (
                    <div className={cn(SURFACE.inner, "mt-1")}>
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Ours now</p>
                        <p className={cn(TYPE.tb, "m-0 mt-0.5 whitespace-pre-line break-words text-gray-600")}>{editMsg.suggestedEdit.before || item.proposed}</p>
                    </div>
                )}
                <div className="mt-2 rounded-xl bg-blue-50 px-3 py-2.5">
                    <p className={cn(TYPE.tc, "m-0 font-medium text-blue-700")}>{name}&rsquo;s wording</p>
                    <p className={cn(TYPE.tb, "m-0 mt-0.5 whitespace-pre-line break-words text-gray-900")}>{editMsg.suggestedEdit.after}</p>
                </div>
            </DrawerBlock>
        );
    } else if (needsState === "changes") {
        block1 = (
            <DrawerBlock label={`${name} asked for a change`} busy={busy === "revise"}>
                <div className="mt-1">{quoteBlock || <p className="m-0 text-gray-600">No words with the request.</p>}</div>
            </DrawerBlock>
        );
    } else if (needsState === "message") {
        block1 = (
            <DrawerBlock label={`Reply to ${name}`} busy={busy === "send"}>
                {quoteBlock && <div className="mb-3 mt-1">{quoteBlock}</div>}
                {composer(`Reply to ${name}`)}
            </DrawerBlock>
        );
    }

    /* Block 2: the other side's words */
    // An approval carries no words of theirs: what they approved is our wording.
    const approvedWording = groupId === "asks.answered" && item.decision === "approved" && !item.answer && !item.fileName && Boolean(item.proposed);
    let block2: ReactNode = null;
    if (!needs && !writing && !draftPack) {
        if (groupId === "asks.waiting" && lastStaff) {
            block2 = (
                <DrawerBlock label="Last from us">
                    <div className="mt-1">
                        <DrawerQuote who={lastStaff.authorName} at={lastStaff.at}>
                            {lastStaff.body}
                        </DrawerQuote>
                    </div>
                </DrawerBlock>
            );
        } else if (approvedWording) {
            block2 = (
                <DrawerBlock label="What they approved">
                    <div className="mt-1 rounded-xl bg-blue-50 px-3 py-2.5">
                        {item.version > 1 && <p className={cn(TYPE.tc, "m-0 font-medium text-blue-700")}>Version {item.version}</p>}
                        <p className={cn(TYPE.tb, "m-0 whitespace-pre-line break-words text-gray-900", item.version > 1 && "mt-0.5")}>{item.proposed}</p>
                    </div>
                </DrawerBlock>
            );
        } else if (groupId === "asks.answered" && (item.answer || item.fileName)) {
            block2 = (
                <DrawerBlock label="Their answer">
                    {item.answer && (
                        <div className="mt-1">
                            <DrawerQuote who={item.decidedBy ?? name} at={item.decidedAt}>
                                {item.answer}
                            </DrawerQuote>
                        </div>
                    )}
                    {item.fileName && (
                        <p className={cn(TYPE.tc, "m-0 mt-2 flex items-center gap-1.5 text-gray-700")}>
                            <FileTextIcon aria-hidden size={14} className="text-red-600" />
                            {item.fileUrl ? (
                                <a href={item.fileUrl} target="_blank" rel="noreferrer" className={cn("rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                                    {item.fileName}
                                    <span className="sr-only"> (opens in a new tab)</span>
                                </a>
                            ) : (
                                item.fileName
                            )}
                        </p>
                    )}
                </DrawerBlock>
            );
        }
    }

    /* Foot */
    const done = flowDone(flow, saidText);
    const footMessage = flowMessage(done, message, saidText);
    // A late question's move is ours: the reminder.
    const lateWaiting = groupId === "asks.waiting" && Boolean(late);
    const writeButton = canWrite && (
        <PillButton
            variant={lateWaiting ? "primary" : undefined}
            disabled={busy !== null}
            onClick={() => {
                setMessage(null);
                setWriting(true);
                // The field appears on this render: focus it once it is there.
                requestAnimationFrame(() => field.focus());
            }}
        >
            Write to {name}
        </PillButton>
    );
    let first: ReactNode = null;
    let second: ReactNode = null;
    if (writing) {
        first = (
            <PillButton variant="primary" icon={SendIcon} busy={busy === "send"} disabled={!draft.trim() && !file} onClick={() => void send(true)}>
                Send message
            </PillButton>
        );
        second = (
            <PillButton disabled={busy !== null} onClick={() => setWriting(false)}>
                Cancel
            </PillButton>
        );
    } else if (needsState === "edit") {
        first = (
            <PillButton variant="primary" busy={busy === "accept"} disabled={busy !== null && busy !== "accept"} onClick={accept}>
                Accept their wording
            </PillButton>
        );
        second = writeButton;
    } else if (needsState === "changes") {
        first = (
            <PillButton variant="primary" icon={SparklesIcon} busy={busy === "revise"} disabled={busy !== null && busy !== "revise"} onClick={revise}>
                Revise the wording and send
            </PillButton>
        );
        second = writeButton;
    } else if (needsState === "message") {
        first = (
            <PillButton variant="primary" icon={SendIcon} busy={busy === "send"} disabled={(!draft.trim() && !file) || (busy !== null && busy !== "send")} onClick={() => void send(false)}>
                Send reply
            </PillButton>
        );
        second = thread && (
            <PillButton disabled={busy !== null} onClick={() => setConfirm("resolve")}>
                Resolve
            </PillButton>
        );
    } else if (!draftPack) {
        if (lateWaiting) first = writeButton;
        else second = writeButton;
    }
    const confirmRow: DrawerConfirm | null =
        confirm === "resolve"
            ? { text: "Resolve it without a reply?", verb: "Resolve", busy: busy === "resolve", onConfirm: resolve, onCancel: () => setConfirm(null) }
            : null;

    const holdsUp = item.holdsUp ?? task?.action ?? null;
    const allMessages = thread?.messages ?? [];

    return (
        <>
            <DrawerHead title={item.title} status={status} chips={<Tag tone={kind.tone}>{kind.label}</Tag>} />

            {block1}
            {block2}

            <DrawerFold id="staff-ask.asked" label="What we asked" note={holdsUp ? `Holds up ${holdsUp}` : undefined} remember>
                <p className="m-0 whitespace-pre-line text-gray-700">{item.why || "No reason written."}</p>
                {item.current && (
                    <div className={cn(SURFACE.inner, "mt-3")}>
                        <p className={cn(TYPE.lab, "m-0")}>On the site now</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-700")}>{item.current}</p>
                    </div>
                )}
                {item.proposed && !approvedWording && (
                    <div className="mt-2 rounded-xl bg-blue-50 px-3 py-2.5">
                        <p className={cn(TYPE.tc, "m-0 font-medium text-blue-700")}>Proposed{item.version > 1 ? `, version ${item.version}` : ""}</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-900")}>{item.proposed}</p>
                    </div>
                )}
                {item.outline && item.outline.length > 0 && (
                    <ul className={cn(TYPE.tc, "list-plain m-0 mt-3 space-y-1 p-0 text-gray-700")}>
                        {item.outline.map((line) => (
                            <li key={line} className="flex gap-2">
                                <span aria-hidden className="text-gray-400">
                                    ·
                                </span>
                                {line}
                            </li>
                        ))}
                    </ul>
                )}
                {item.options && item.options.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {item.options.map((o) => (
                            <Tag key={o} tone="teal">
                                {o}
                            </Tag>
                        ))}
                    </div>
                )}
                {holdsUp && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>Holds up {holdsUp}</p>}
            </DrawerFold>

            {allMessages.length > 0 && (
                <DrawerFold id="staff-ask.thread" label={`Conversation · ${allMessages.length}`} note={thread?.status === "resolved" ? "Resolved" : undefined} remember>
                    <StaffThreadMessages thread={thread} />
                </DrawerFold>
            )}

            <div className="px-1">
                <ArrowLink onClick={() => onSub({ kind: "pack", packId: pack.id })}>
                    {draftPack ? `In ${pack.title}` : `Sent in ${pack.title}${dayDate(pack.publishedAt) ? ` · ${dayDate(pack.publishedAt)}` : ""}`}
                </ArrowLink>
            </div>

            <DrawerFoot message={footMessage} done={done} confirm={confirmRow} first={done ? undefined : first || undefined} second={done ? undefined : second || undefined} />
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * A-F: a call they asked for
 * ------------------------------------------------------------------------- */

function CallSheet({
    call,
    item,
    flow,
    onSub,
    onChanged,
}: {
    call: CallRequest;
    item: PackItem | null;
    flow: SheetFlow;
    onSub: (t: AskTarget) => void;
    onChanged: () => Promise<void>;
}) {
    const [saidText, setSaid] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [busy, setBusyState] = useState<"scheduled" | "closed" | null>(null);
    const [confirm, setConfirm] = useState(false);
    const voice = useSheetVoice(setSaid, setMessage);
    const name = requesterName(call);

    const settle = async (next: "scheduled" | "closed") => {
        if (busy) return;
        setBusyState(next);
        flow.onBusy?.(true);
        setMessage(null);
        setSaid(null);
        try {
            await updateCallRequest(call.id, next);
            await onChanged();
            voice.said(next === "scheduled" ? `Marked scheduled. Email ${name} the time.` : "Closed.");
        } catch {
            voice.note({ tone: "error", text: "That did not save. Try again in a moment." });
        } finally {
            setBusyState(null);
            flow.onBusy?.(false);
            setConfirm(false);
        }
    };

    const asked = askedText(call.at);
    const status: DrawerStatus =
        call.status === "scheduled"
            ? { tone: ASK_GROUP["requests.scheduled"].tone, label: "Scheduled", clock: asked ? { text: asked } : null }
            : call.status === "closed"
                ? { tone: ASK_GROUP["requests.done"].tone, label: "Done", clock: asked ? { text: asked } : null }
                : { tone: ASK_GROUP["requests.needs_us"].tone, label: "Needs us", clock: asked ? { text: asked } : null };
    const note = call.note.trim();

    const done = flowDone(flow, saidText);
    const footMessage = flowMessage(done, message, saidText);
    let first: ReactNode = null;
    let second: ReactNode = null;
    if (call.status === "open") {
        first = (
            <PillButton variant="primary" busy={busy === "scheduled"} disabled={busy !== null} onClick={() => void settle("scheduled")}>
                Mark scheduled
            </PillButton>
        );
        second = (
            <PillButton disabled={busy !== null} onClick={() => setConfirm(true)}>
                Close the request
            </PillButton>
        );
    } else if (call.status === "scheduled") {
        second = (
            <PillButton busy={busy === "closed"} disabled={busy !== null} onClick={() => void settle("closed")}>
                Close the request
            </PillButton>
        );
    }

    return (
        <>
            <DrawerHead lead={<PhoneIcon aria-hidden size={14} className={AREA_TEXT.people} />} title={`${name} asked for a call`} status={status} />
            <DrawerBlock label={call.status === "open" ? "Book the call" : `${name} asked`} busy={busy !== null}>
                <ul className="m-0 mt-1 list-plain space-y-2 p-0 text-gray-700">
                    {call.preference && (
                        <li className="flex items-start gap-2">
                            <CalendarClockIcon aria-hidden size={14} className={cn("mt-[3px] shrink-0", AREA_TEXT.dates)} />
                            <span className="min-w-0 break-words">Suits them: {call.preference.toLowerCase()}</span>
                        </li>
                    )}
                    {item && (
                        <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="min-w-0 break-words">About &ldquo;{item.title}&rdquo;</span>
                            <PillButton variant="text" onClick={() => onSub({ kind: "item", itemId: item.id })}>
                                Open the question
                            </PillButton>
                        </li>
                    )}
                </ul>
                {note && (
                    <div className="mt-3">
                        <DrawerQuote who={name} at={call.at}>
                            {note}
                        </DrawerQuote>
                    </div>
                )}
            </DrawerBlock>
            <DrawerFoot
                message={footMessage}
                done={done}
                confirm={confirm ? { text: "Close this request without a call?", verb: "Close it", busy: busy === "closed", onConfirm: () => void settle("closed"), onCancel: () => setConfirm(false) } : null}
                first={done ? undefined : first || undefined}
                second={done ? undefined : second || undefined}
            />
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * A-E: a change they asked for
 * ------------------------------------------------------------------------- */

type ChangeBusy = "reply" | "small" | "done" | null;

/** How the client's original message reached us, for "What they sent". */
const SENT_VIA: Record<NonNullable<ChangeRequest["source"]>, string> = {
    oura: "Asked in Oura",
    text: "Pasted message",
    screenshot: "Screenshot",
    email: `Email to ${OURA_INBOX}`,
};

/** A change request's title is its words, cut here; the body quotes the whole note only past this. */
const CHANGE_TITLE_MAX = 100;

/**
 * A change the client asked for from their Work page. Reply emails the client
 * from oura@ (the server sends it and rings their bell); a reply never
 * finishes it, since the change is still ours to make. "File as September's
 * small change" files it under G5, and asks in the foot first when the month
 * already has one. Done finishes it.
 */
function ChangeSheet({
    change,
    all,
    hasG5,
    flow,
    draft,
    setDraft,
    onChanged,
}: {
    change: ChangeRequest;
    /** Every request for this client, to name the one already filed this month. */
    all: ChangeRequest[];
    hasG5: boolean | null;
    flow: SheetFlow;
    draft: string;
    setDraft: (text: string) => void;
    onChanged: () => Promise<void>;
}) {
    const signature = useStaffSignature();
    /** The request as the last write here returned it: newer than the screen's list until that reloads. */
    const [local, setLocal] = useState<ChangeRequest | null>(null);
    const [saidText, setSaid] = useState<string | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [replyError, setReplyError] = useState<string | null>(null);
    const [busy, setBusyState] = useState<ChangeBusy>(null);
    const [confirm, setConfirm] = useState<{ kind: "done" } | { kind: "small"; existing: ChangeRequest | null } | null>(null);
    const [writeAgain, setWriteAgain] = useState(false);
    /** Screenshots on the reply being written: uploaded only on Send reply. */
    const [pics, setPics] = useState<File[]>([]);
    const voice = useSheetVoice(setSaid, setMessage);

    const c = local ?? change;
    const name = requesterName(c);
    const isDone = c.status === "done";
    const composing = !isDone && (!c.reply || writeAgain);
    const field = useFieldRef(composing);
    const text = draft.trim();
    const month = malaysianMonthNow();
    const monthWord = monthName(month) ?? "this month";
    const filedMonth = monthName(c.smallChange?.month) ?? monthWord;
    // Filed after the month already had its small change: an extra.
    const extra = extraSmallChangeIds([...all.filter((x) => x.id !== c.id), c]).has(c.id);

    const setBusy = (b: ChangeBusy) => {
        setBusyState(b);
        flow.onBusy?.(b !== null);
    };

    /** Runs one write; the Requests card and the screen read again after it. */
    const write = async (what: Exclude<ChangeBusy, null>, work: () => Promise<ChangeRequest>): Promise<ChangeRequest> => {
        setBusy(what);
        try {
            const updated = await work();
            setLocal(updated);
            changeRequestsChanged();
            return updated;
        } finally {
            setBusy(null);
        }
    };

    const sendReply = async () => {
        if (!text || busy) return;
        setReplyError(null);
        setMessage(null);
        setSaid(null);
        try {
            const updated = await write("reply", async () => {
                const keys: string[] = [];
                for (const f of pics) keys.push(await uploadReplyPicture(f));
                return replyToChangeRequest(c.id, text, keys);
            });
            setDraft("");
            setPics([]);
            setWriteAgain(false);
            const by = updated.reply?.by;
            const withPics = pics.length > 0 ? ` with ${picturesWord(pics.length)}` : "";
            // The server emails a reply only to a request with a client address (#4724):
            // one staff added (pasted, a screenshot, an email to oura@) has none.
            voice.note({
                tone: "success",
                text: updated.requesterEmail?.trim()
                    ? `Reply${withPics} emailed from ${OURA_INBOX}${by ? `, signed ${by}` : ""}.`
                    : `Reply${withPics} saved. No email was sent: this request has no client email address.`,
            });
            void onChanged();
        } catch (err) {
            setReplyError(requestErrorText(err, "Your reply did not send. It is still here. Try again."));
            field.focus();
        }
    };

    const fileSmallChange = async (confirmOverLimit: boolean) => {
        if (busy) return;
        setMessage(null);
        // The month's small change is already filed: ask in the foot before calling.
        if (!confirmOverLimit) {
            const taken = all.find((x) => x.id !== c.id && x.smallChange?.month === month) ?? null;
            if (taken) {
                setConfirm({ kind: "small", existing: taken });
                return;
            }
        }
        try {
            await write("small", () => updateChangeRequest(c.id, confirmOverLimit ? { smallChange: true, confirmOverLimit: true } : { smallChange: true }));
            setConfirm(null);
            voice.note({ tone: "success", text: `Filed as ${monthWord}'s small change, under G5.` });
            void onChanged();
        } catch (err) {
            const e = err as { code?: string | null; existingId?: string | null } | null;
            if (e?.code === "small_change_already_filed" && !confirmOverLimit) {
                setConfirm({ kind: "small", existing: all.find((x) => x.id === e.existingId) ?? null });
                return;
            }
            setConfirm(null);
            voice.note({ tone: "error", text: requestErrorText(err, "It was not filed. Try again.") });
        }
    };

    const markDone = async () => {
        if (busy) return;
        setMessage(null);
        setSaid(null);
        try {
            await write("done", () => updateChangeRequest(c.id, "done"));
            await onChanged();
            setConfirm(null);
            voice.said("Marked done.");
        } catch (err) {
            setConfirm(null);
            voice.note({ tone: "error", text: requestErrorText(err, "It was not marked done. Try again.") });
        }
    };

    /* Header */
    // The title is their words, as the row shows them, so walking the group
    // reads as walking the requests. The body quotes the rest only when the
    // title had to cut it.
    const note = c.note.trim();
    const cutNote = note.length > CHANGE_TITLE_MAX;
    const title = `\u201c${trimText(note, CHANGE_TITLE_MAX)}\u201d`;
    // What Oura read out of their message: the brief, and their words kept whole.
    const sent = c.original?.text?.trim() || "";
    const hasBrief = Boolean(c.why?.trim() || (c.checklist?.length ?? 0) > 0 || (c.notes?.length ?? 0) > 0);
    const briefBlock = (hasBrief || c.where) && (
        <DrawerBlock label="The brief">
            {c.where && <p className={cn(TYPE.tc, "m-0 mb-3 text-gray-500")}>Where: {c.where}</p>}
            <Brief why={c.why || undefined} checklist={c.checklist} notes={c.notes} />
        </DrawerBlock>
    );
    const sentBlock = sent && sent !== note && (
        <DrawerBlock label="What they sent">
            <div className="mt-1">
                <ClientMessage who={name} via={SENT_VIA[c.source ?? "oura"]} when={dayDate(c.at) ?? ""} subject={c.original?.email?.subject || undefined} text={sent} />
            </div>
        </DrawerBlock>
    );
    const replied = c.reply ? repliedText(c.reply.at) : null;
    const asked = askedText(c.at);
    const askedBy = asked ? `${name} ${asked.charAt(0).toLowerCase()}${asked.slice(1)}` : null;
    const status: DrawerStatus = isDone
        ? { tone: ASK_GROUP["requests.done"].tone, label: "Done", clock: dayDate(c.doneAt) ? { text: dayDate(c.doneAt) as string } : null }
        : { tone: ASK_GROUP["requests.needs_us"].tone, label: "Needs us", clock: replied ? { text: replied } : askedBy ? { text: askedBy } : null };
    const email = c.requesterEmail?.trim() || null;
    const names = c.files.map((f) => f.name).filter(Boolean);
    const chips = (c.smallChange || c.files.length > 0) && (
        <>
            {c.smallChange && <FamilyChip family="g">G5</FamilyChip>}
            <Clip count={c.files.length} names={names} />
        </>
    );

    /* The small change: only where G5 is in the contract (or the contract could not be read). */
    const smallLine = hasG5 !== false && (c.smallChange || !isDone) && (
        <p className={cn(TYPE.tc, "m-0 mt-3 flex flex-wrap items-center gap-x-2 text-gray-500")}>
            {c.smallChange ? (
                <span title="Under 30 minutes, no new pages, no design">
                    Filed for {filedMonth}
                    {extra ? ", on top of the plan's 1" : ""}
                    {c.smallChange.filedBy || c.smallChange.filedAt ? ` · by ${[c.smallChange.filedBy, dayDate(c.smallChange.filedAt)].filter(Boolean).join(", ")}` : ""}
                </span>
            ) : (
                <PillButton variant="text" busy={busy === "small"} disabled={busy !== null || confirm !== null} onClick={() => void fileSmallChange(false)}>
                    File as {monthWord}&rsquo;s small change
                </PillButton>
            )}
        </p>
    );

    // More than the small change: extra work on the plan, a line on Delivery.
    const extraLine = DELIVERY_ON && !isDone && <AddAsExtraWork key={`${c.businessId}:${c.id}`} businessId={c.businessId} title={c.title?.trim() || trimText(note, 120)} />;

    const fullQuote = cutNote && (
        <div className="mt-1">
            <DrawerQuote who={name} at={c.at}>
                {note}
            </DrawerQuote>
        </div>
    );
    const askedBlock = (
        <DrawerBlock label={`${name} asked`}>
            {fullQuote || <p className={cn(TYPE.tc, "m-0 mt-0.5 break-words text-gray-500")}>{[dayDate(c.at), email].filter(Boolean).join(" · ")}</p>}
            <FileList files={c.files} />
            {smallLine}
            {extraLine}
        </DrawerBlock>
    );
    // Every reply, oldest first: a later one no longer replaces the earlier (#6874).
    const replies = everyReply(c);
    const ourReply = replies.length > 0 && (
        <DrawerBlock label={replies.length > 1 ? "Our replies" : "Our reply"}>
            {replies.map((sent, i) => (
                <div key={`${sent.at}:${i}`} className="mt-1">
                    <DrawerQuote who={sent.by || "We"} where={`from ${OURA_INBOX}`} at={sent.at}>
                        {sent.body}
                    </DrawerQuote>
                    <ReplyPictureGrid files={sent.files} />
                </div>
            ))}
        </DrawerBlock>
    );
    const composer = (
        <StaffComposer
            value={draft}
            onChange={(t) => {
                setDraft(t);
                if (replyError) setReplyError(null);
            }}
            label={`Reply to ${name}`}
            placeholder="Your reply"
            hint={
                email
                    ? `Emails ${email} from ${OURA_INBOX}${signature ? `, signed ${signature}` : ""}${pics.length > 0 ? `, with ${picturesWord(pics.length)}` : ""}.`
                    : "No email is sent for this request: it has no client email address."
            }
            pictures={
                REPLY_PICTURES_ON
                    ? {
                          files: pics,
                          onFiles: setPics,
                          onError: (t) => setReplyError(t),
                          disabled: busy !== null,
                      }
                    : undefined
            }
            onSubmit={() => void sendReply()}
            fieldRef={field.ref}
            error={replyError}
        />
    );

    /* Foot */
    const done = flowDone(flow, saidText);
    const footMessage = flowMessage(done, message, saidText);
    let first: ReactNode = null;
    let second: ReactNode = null;
    if (composing) {
        first = (
            <PillButton variant="primary" icon={SendIcon} busy={busy === "reply"} disabled={!text || (busy !== null && busy !== "reply")} onClick={() => void sendReply()}>
                Send reply
            </PillButton>
        );
        second = writeAgain ? (
            <PillButton disabled={busy !== null} onClick={() => setWriteAgain(false)}>
                Cancel
            </PillButton>
        ) : (
            <PillButton disabled={busy !== null} onClick={() => setConfirm({ kind: "done" })}>
                Done
            </PillButton>
        );
    } else if (!isDone) {
        first = (
            <PillButton variant="primary" busy={busy === "done"} disabled={busy !== null && busy !== "done"} onClick={() => void markDone()}>
                Done
            </PillButton>
        );
        second = (
            <PillButton
                disabled={busy !== null}
                onClick={() => {
                    setMessage(null);
                    setWriteAgain(true);
                    requestAnimationFrame(() => field.focus());
                }}
            >
                Write again
            </PillButton>
        );
    }
    let confirmRow: DrawerConfirm | null = null;
    if (confirm?.kind === "done") {
        confirmRow = { text: `Close it without a reply? ${name} gets no email.`, verb: "Close it", busy: busy === "done", onConfirm: () => void markDone(), onCancel: () => setConfirm(null) };
    } else if (confirm?.kind === "small") {
        const note = confirm.existing ? `: “${trimText(confirm.existing.note, 60)}”` : "";
        confirmRow = {
            text: `${monthWord}'s small change is already filed${note}. File this one too?`,
            verb: "File it anyway",
            busy: busy === "small",
            onConfirm: () => void fileSmallChange(true),
            onCancel: () => setConfirm(null),
        };
    }

    return (
        <>
            <DrawerHead lead={<MessageSquarePlusIcon aria-hidden size={14} className={AREA_TEXT.asks} />} title={title} clampTitle status={status} chips={chips || undefined} />

            {briefBlock}
            {sentBlock}
            {composing && !writeAgain ? (
                // Block 1 holds everything the reply needs: their words, their files, the field.
                <DrawerBlock label={`Reply to ${name}`} busy={busy === "reply"}>
                    {fullQuote}
                    <FileList files={c.files} />
                    <div className={fullQuote || c.files.length > 0 ? "mt-3" : "mt-1"}>{composer}</div>
                    {smallLine}
                    {extraLine}
                </DrawerBlock>
            ) : writeAgain ? (
                <>
                    <DrawerBlock label={`Write to ${name}`} busy={busy === "reply"}>
                        <div className="mt-1">{composer}</div>
                    </DrawerBlock>
                    {askedBlock}
                    {ourReply}
                </>
            ) : (
                <>
                    {askedBlock}
                    {ourReply}
                </>
            )}

            <DrawerFoot message={footMessage} done={done} confirm={confirmRow} first={done ? undefined : first || undefined} second={done ? undefined : second || undefined} />
        </>
    );
}
